import { ContentResourceResponse } from "@ctv/core/resource";
import { formatMessage, Locale } from "@ctv/shared-core/src";
import { isCorporateName, isName } from "@ctv/shared-core/src/utils/validation";
import { z, ZodType } from "zod";
import {
  BILLING_CYCLES,
  ExistingProductTypes,
  INDUSTRIES,
  JOB_TITLES,
  POSITIONS,
  RequestProductType,
  TERMS_OF_PAYMENTS,
  TRAVEL_SPENDINGS,
} from "../../_constants";
import { PaymentMethod, RegistrationFormValue } from "../../types";

function schemaValidationStep1(
  cr: ContentResourceResponse["CorporateRegistration"]
): ZodType<Partial<RegistrationFormValue>> {
  return z
    .object({
      picName: z
        .string()
        .nonempty(cr.picNameRequired)
        .max(256, cr.picNameMaxLength)
        .refine(
          (value) => isName(value, true),
          formatMessage(cr.validationAlphabetOnly, {
            field: cr.picNameLabelV2,
          })
        ),
      phone: z
        .object({
          prefix: z.string(),
          phone: z.string(),
        })
        .refine((value) => value.prefix.length, {
          message: cr.picPhoneRequired,
        })
        .refine((value) => value.phone.length, {
          message: cr.picPhoneRequired,
        })
        .refine((value) => value.phone.length > 7, {
          message: cr.picPhoneMinLength,
        })
        .refine((value) => value.phone.length < 15, {
          message: cr.picPhoneMaxLength,
        })
        .refine((value) => /^[0-9]+\s*$/.test(value.phone), {
          message: cr.picPhoneSpecialCharacter,
        }),
      picEmail: z.string(),
      picJobTitle: z
        .string()
        .nonempty(cr.jobTitleRequired)
        .refine((value) => JOB_TITLES.includes(value), {
          message: cr.jobTitleRequired,
        }),
      companyName: z
        .string()
        .nonempty(cr.companyNameRequired)
        .max(256, cr.companyNameMaxLength)
        .refine((value) => isCorporateName(value, true), {
          message: formatMessage(cr.validationAlphabetOnly, {
            field: cr.companyNameSpecialCharacter,
          }),
        }),
      companyIndustry: z
        .string()
        .nonempty(cr.companyIndustryRequired)
        .refine((value) => INDUSTRIES.includes(value), {
          message: cr.companyIndustryRequired,
        }),
      country: z.string().nonempty(cr.countryRequired),
      officeAddress: z
        .string()
        .nonempty(cr.officeAddressRequired)
        .max(512, cr.officeAddressMaxLength)
        .regex(/^[A-Za-z0-9_'.\-\s,/]*$/, {
          message: cr.officeAddressSpecialCharacter,
        }),
      officeCity: z
        .string()
        .nonempty(cr.cityRequired)
        .max(128, cr.cityMaxLength)
        .refine((value) => isName(value, true), {
          message: cr.citySpecialCharacter,
        }),
      officePostalCode: z
        .string()
        .nonempty(cr.postalCodeRequired)
        .max(32, cr.postalCodeMaxLength)
        .regex(/^[0-9]*$/, {
          message: cr.postalCodeSpecialCharacter,
        }),
      officePhone: z
        .object({
          prefix: z.string(),
          phone: z.string(),
        })
        .refine((value) => value.prefix.length, {
          message: cr.officePhoneRequired,
        })
        .refine((value) => value.phone.length, {
          message: cr.officePhoneRequired,
        })
        .refine((value) => value.phone.length > 7, {
          message: cr.officePhoneMinLength,
        })
        .refine((value) => value.phone.length < 15, {
          message: cr.officePhoneMaxLength,
        })
        .refine((value) => /^[0-9]+\s*$/.test(value.phone), {
          message: cr.officePhoneSpecialCharacter,
        }),
      website: z.string().optional(),
      monthlyTravelSpending: z
        .string()
        .nonempty(cr.expensesRequired)
        .refine((value) => TRAVEL_SPENDINGS.includes(value), {
          message: cr.expensesRequired,
        }),
      productNeeded: z
        .array(z.nativeEnum(ExistingProductTypes))
        .nonempty(cr.productRequired),
      productRequest: z
        .array(z.nativeEnum(RequestProductType), {
          required_error: cr.productRequired,
        })
        .optional(),
    })
    .partial();
}

function schemaValidationStep3(
  cr: ContentResourceResponse["CorporateRegistration"],
  locale: Locale
): ZodType<Partial<RegistrationFormValue>> {
  const baseSchema = z.object({
    paymentMethod: z.nativeEnum(PaymentMethod),
    ownerName: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.directorAttorneyFullNameLabel,
        })
      )
      .refine(
        (value) => isName(value, true),
        formatMessage(cr.validationAlphabetOnly, {
          field: cr.directorAttorneyFullNameLabel,
        })
      ),
    ownerJobTitle: z
      .string()
      .nonempty(cr.jobTitleRequired)
      .refine(
        (value) => POSITIONS.includes(value),
        formatMessage(cr.validationRequired, {
          field: cr.directorAttorneyPositionLabel,
        })
      ),
  });

  const creditCardSchema = baseSchema.extend({
    paymentMethod: z.literal(PaymentMethod.CREDIT_CARD),
    agreementDocument: z.record(z.string(), z.any()).and(
      z.object({
        AGREEMENT_FORM_CC: z
          .object({
            url: z.string().optional(),
            file: z
              .object({
                name: z.string(),
                size: z.number(),
              })
              .optional(),
          })
          .optional()
          .refine((value) => value?.url, cr.documentRequired)
          .refine(
            (value) => {
              const allowedExtensions = ["pdf", "png", "jpeg"];
              return (
                value?.file &&
                allowedExtensions.includes(
                  value.file.name.split(".").pop()?.toLowerCase() ?? ""
                )
              );
            },
            formatMessage(cr.documentExtension, {
              val: [".pdf", ".png", ".jpeg"].join(", "),
            })
          )
          .refine(
            (value) => value?.file && value.file.size < Number(20) * 1e6,
            formatMessage(cr.documentFileSize, {
              val: 20,
            })
          ),
      })
    ),
  });

  const invoiceSchema = baseSchema.extend({
    paymentMethod: z.literal(PaymentMethod.INVOICE),
    serviceFee: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.paymentProposalServiceFeeLabel,
        })
      )
      .transform((v) => v.trim())
      .refine((v) => !isNaN(Number(v)), {
        message: formatMessage(cr.validationNumericOnly, {
          field: cr.paymentProposalServiceFeeLabel,
        }),
      })
      .transform((v) => Number(v))
      .refine((v) => v > 0, {
        message: formatMessage(cr.validationMinValue, { value: 0 }),
      })
      .refine((v) => v < 100, {
        message: formatMessage(cr.validationMaxValue, { value: 100 }),
      })
      .transform((v) => String(v)),
    billingCycle: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.paymentProposalBillingCycleLabel,
        })
      )
      .refine(
        (v) => BILLING_CYCLES.includes(v),
        formatMessage(cr.validationRequired, {
          field: cr.paymentProposalBillingCycleLabel,
        })
      ),
    termsOfPayment: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.paymentProposalTermsOfPaymentLabel,
        })
      )
      .refine(
        (v) => Object.values(TERMS_OF_PAYMENTS).includes(v),
        formatMessage(cr.validationRequired, {
          field: cr.paymentProposalTermsOfPaymentLabel,
        })
      ),
    creditLimit: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.paymentProposalCredtLimitLabel,
        })
      )
      .regex(
        /^[0-9]+\s*$/,
        formatMessage(cr.validationNumericOnly, {
          field: cr.paymentProposalCredtLimitLabel,
        })
      )
      .transform((v) => Number(v))
      .refine((v) => v > 0, {
        message: formatMessage(cr.validationMinValue, { value: 0 }),
      })
      .refine((v) => v < 999_999_999_999, {
        message: formatMessage(cr.validationMaxValue, {
          value: Number(999_999_999_999).toLocaleString(locale),
        }),
      })
      .transform((v) => String(v)),
    legalNpwpNumber: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.legalDetailsNpwpNumberLabel,
        })
      )
      .regex(
        /^[0-9]+\s*$/,
        formatMessage(cr.validationNumericOnly, {
          field: cr.legalDetailsNpwpNumberLabel,
        })
      )
      .min(
        15,
        formatMessage(cr.validationMinLength, {
          value: 15,
        })
      )
      .max(
        15,
        formatMessage(cr.validationMaxLength, {
          value: 15,
        })
      ),
    legalSppkpNumber: z.string().nonempty(
      formatMessage(cr.validationRequired, {
        field: cr.legalDetailsSppkpNumberLabel,
      })
    ),
    legalName: z.string().nonempty(
      formatMessage(cr.validationRequired, {
        field: cr.legalDetailsCorporateNameLabel,
      })
    ),
    legalFiscalAddress: z.string().nonempty(
      formatMessage(cr.validationRequired, {
        field: cr.legalDetailsBusinessAddressLabel,
      })
    ),
    legalFiscalCity: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.legalDetailsCityPlaceholder,
        })
      )
      .refine(
        (value) => isName(value, true),
        formatMessage(cr.validationAlphabetOnly, {
          field: cr.legalDetailsCityPlaceholder,
        })
      ),
    legalFiscalPostalCode: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.legalDetailsPostalCodePlaceholder,
        })
      )
      .regex(
        /^[0-9]+\s*$/,
        formatMessage(cr.validationNumericOnly, {
          field: cr.legalDetailsPostalCodePlaceholder,
        })
      )
      .min(
        5,
        formatMessage(cr.validationMinLength, {
          value: 5,
        })
      )
      .max(
        5,
        formatMessage(cr.validationMaxLength, {
          value: 5,
        })
      ),
    legalBillingAddress: z.string().nonempty(
      formatMessage(cr.validationRequired, {
        field: cr.legalDetailsBillingAddressLabel,
      })
    ),
    legalBillingCity: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.legalDetailsCityPlaceholder,
        })
      )
      .refine(
        (value) => isName(value, true),
        formatMessage(cr.validationAlphabetOnly, {
          field: cr.legalDetailsCityPlaceholder,
        })
      ),
    legalBillingPostalCode: z
      .string()
      .nonempty(
        formatMessage(cr.validationRequired, {
          field: cr.legalDetailsPostalCodePlaceholder,
        })
      )
      .regex(
        /^[0-9]+\s*$/,
        formatMessage(cr.validationNumericOnly, {
          field: cr.legalDetailsPostalCodePlaceholder,
        })
      )
      .min(
        5,
        formatMessage(cr.validationMinLength, {
          value: 5,
        })
      )
      .max(
        5,
        formatMessage(cr.validationMaxLength, {
          value: 5,
        })
      ),
  });

  return z.discriminatedUnion("paymentMethod", [
    creditCardSchema,
    invoiceSchema,
  ]);
}

export function registerSchemaValidation(
  step: number,
  locale: Locale,
  cr: ContentResourceResponse["CorporateRegistration"]
): ZodType<Partial<RegistrationFormValue>> {
  if (step === 0) {
    return schemaValidationStep1(cr);
  } else if (step === 2) {
    return schemaValidationStep3(cr, locale);
  }
  return z.object({});
}
